import { updateMFAPreference, verifyTOTPSetup } from 'aws-amplify/auth';
import { Formik } from 'formik';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useState } from 'react';
import images from '../../assets/images';
import OTPField from '../../components/common/OTPField';
import { setAuthPreference } from '../../services/auth-setup/auth-setup-service';
import { getProfile } from '../../services/user/user-service';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import { AccountMigrationOTPSchema } from '../../validation/login/login-validation';
import { userDetail } from '../../store/actions/app.Actions';

function AuthAppSetupOTP() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  const handleBackBtnClick = () => {
    navigate('/auth/select-auth-option', { replace: true });
  };

  const handleNavigationAfterMFASetup = async () => {
    try {
      sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
      sessionStorage.removeItem(Common.SIGNUP_CREDENTIALS);
      localStorage.removeItem(Common.RESET_AUTH_METHOD);
      localStorage.removeItem(Common.IS_PASSKEY_FAILED);

      const response = await getProfile();
      dispatch(userDetail(response.Data));

      const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';

      if (downloadVATRedirect) {
        sessionStorage.removeItem('vat_receipt_path');
        navigate(downloadVATRedirect, { replace: true });
        return;
      }
      if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
        const referralCode = localStorage.getItem('referralCode');
        navigate(Common.Routes.REFERRAL_PROMOTION_PREFERENCE, {
          state: { referralCode: referralCode },
          replace: true,
        });
        return;
      }
      if (!response?.Data.is_profile_verified) {
        navigate(Common.Routes.PROFILE_REDIRECT, { replace: true });
        return;
      }

      if (response?.Data.payment_type === null) {
        navigate(Common.Routes.PAYMENT, { replace: true });
        return;
      }

      navigate(Common.Routes.HOME, { replace: true });
    } catch (error) {
      sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
      sessionStorage.removeItem(Common.SIGNUP_CREDENTIALS);
      toast.error(t('login.toast.error'));
      navigate(Common.Routes.HOME, { replace: true });
    }
  };

  const handleConfirmTOTP = async (code: string) => {
    setIsSubmittingOTP(true);
    try {
      await verifyTOTPSetup({ code });

      await updateMFAPreference({
        totp: 'PREFERRED',
      });

      await setAuthPreference('TOTP');

      if (sessionStorage.getItem(Common.CHANGE_AUTH_METHOD) === 'true') {
        navigate('/security', { replace: true });
        sessionStorage.removeItem(Common.CHANGE_AUTH_METHOD);
        sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
        return;
      }

      await handleNavigationAfterMFASetup();
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message.includes('Code mismatch')) {
        toast.error(t('signup.toast.invalidCode'));
      } else {
        toast.error(error.toString());
      }
    } finally {
      setIsSubmittingOTP(false);
    }
  };

  return (
    <div className="auth-options-wrapper auth-app-setup-OTP-wrapper">
      <Helmet>
        <title>{companyNameReplace(t('authentication.title'))} </title>
      </Helmet>
      <div className="auth-options-container auth-app-setup-OTP-container">
        <div className="auth-header">
          <div className="auth-icon-container">
            <img src={images.authAppIconSVG} alt="Security" className="auth-icon-image" />
          </div>
          <h1 className="auth-title">{t('authentication.authAppOTP.header')}</h1>
          <p className="auth-subtitle auth-app-otp-subtitle">{t('authentication.authAppOTP.subHeader')}</p>
        </div>

        <Formik
          initialValues={{ code: '' }}
          onSubmit={async (values) => {
            if (isSubmittingOTP) return;
            await handleConfirmTOTP(values.code);
          }}
          validationSchema={AccountMigrationOTPSchema(t)}
        >
          {({ handleSubmit }) => (
            <form
              onSubmit={handleSubmit}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isSubmittingOTP) {
                  event.preventDefault();
                }
              }}
            >
              <div className="app-setup-otp-container p-1 mb-3">
                <OTPField name="code" customStyleClass="app-setup-otp" />
              </div>

              <div className="auth-app-button-OTP-wrapper">
                <button type="submit" className="btn btn-primary auth-app-button" disabled={isSubmittingOTP}>
                  {t('authentication.button.continue')}
                </button>
                <p className="different-auth-method" onClick={handleBackBtnClick}>
                  {t('authentication.button.differentMethod')}
                </p>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default AuthAppSetupOTP;
